import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { saveAs } from 'file-saver';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzTagModule } from 'ng-zorro-antd/tag';

import { PropertyImportJobStatusResponse } from '../models/property-import.models';
import { PropertyImportService } from '../services/property-import.service';
import {
  translatePropertyImportSampleErrors,
  translatePropertyImportJobMessage,
} from '../utils/property-import-message.utils';

@Component({
  selector: 'app-property-import-progress',
  standalone: true,
  imports: [CommonModule, TranslateModule, NzAlertModule, NzButtonModule, NzIconModule, NzProgressModule, NzTagModule],
  templateUrl: './property-import-progress.component.html',
  styleUrls: ['./property-import-progress.component.less'],
})
export class PropertyImportProgressComponent {
  @Input() job: PropertyImportJobStatusResponse | null = null;
  @Input() dismissible = false;
  @Input() autoDismissCountdownSeconds: number | null = null;
  @Output() dismiss = new EventEmitter<void>();
  downloadingErrorReport = false;

  constructor(
    private translate: TranslateService,
    private propertyImportService: PropertyImportService
  ) {}

  get isVisible(): boolean {
    return !!this.job;
  }

  get isProcessingStage(): boolean {
    return this.job?.status === 'processing' && Number(this.job?.totalRows || 0) > 0;
  }

  get isTerminal(): boolean {
    return ['completed', 'completed_with_errors', 'failed'].includes(this.job?.status || '');
  }

  get isSuccess(): boolean {
    return this.job?.status === 'completed';
  }

  get isPartial(): boolean {
    return this.job?.status === 'completed_with_errors';
  }

  get isFailure(): boolean {
    return this.job?.status === 'failed';
  }

  get progressPercent(): number {
    return Number(this.job?.percentComplete || 0);
  }

  get errorPreview(): string[] {
    return translatePropertyImportSampleErrors(this.job, this.translate, 5);
  }

  get canDownloadErrorReport(): boolean {
    return !!this.job?.jobId && !!this.job?.hasErrorReport && Number(this.job?.errorReportRowCount || 0) > 0;
  }

  get translatedJobMessage(): string {
    return translatePropertyImportJobMessage(this.job, this.translate, 'PROPERTY_IMPORT.FAILED_MESSAGE');
  }

  get successAlertMessage(): string {
    return translatePropertyImportJobMessage(this.job, this.translate, 'PROPERTY_IMPORT.SUCCESS_MESSAGE');
  }

  get failureAlertMessage(): string {
    return this.translatedJobMessage;
  }

  get failureAlertDescription(): string | null {
    if (!this.isFailure || !this.errorPreview.length) {
      return null;
    }

    return this.errorPreview[0];
  }

  get partialAlertMessage(): string {
    return translatePropertyImportJobMessage(this.job, this.translate, 'PROPERTY_IMPORT.PARTIAL_MESSAGE');
  }

  get partialAlertDescription(): string | null {
    if (!this.isPartial) {
      return null;
    }

    return null;
  }

  get shouldShowProgressMessage(): boolean {
    if (!this.job) {
      return false;
    }

    if (this.isTerminal) {
      return false;
    }

    return !!translatePropertyImportJobMessage(this.job, this.translate, '');
  }

  get progressMessageText(): string {
    return translatePropertyImportJobMessage(this.job, this.translate, '');
  }

  get stageLabelKey(): string {
    return `PROPERTY_IMPORT.STAGES.${this.job?.currentStage || 'queued'}`;
  }

  private formatErrorReportDate(date: Date): string {
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }

  private getDownloadFileName(contentDisposition: string | null | undefined): string {
    const match = String(contentDisposition || '').match(/filename="([^"]+)"/i);
    return match?.[1] || `Objektimport-Fehlerbericht-${this.formatErrorReportDate(new Date())}.xlsx`;
  }

  downloadErrorReport(): void {
    if (!this.job?.jobId || !this.canDownloadErrorReport || this.downloadingErrorReport) {
      return;
    }

    this.downloadingErrorReport = true;
    this.propertyImportService
      .downloadImportJobErrorsWorkbook(this.job.jobId, this.translate.currentLang || 'en')
      .subscribe({
        next: (response) => {
          saveAs(response.body || new Blob(), this.getDownloadFileName(response.headers.get('content-disposition')));
          this.downloadingErrorReport = false;
        },
        error: () => {
          this.downloadingErrorReport = false;
        },
      });
  }

  onDismiss(): void {
    if (!this.dismissible) {
      return;
    }
    this.dismiss.emit();
  }
}
